import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { format, isPast, isToday, parseISO, isTomorrow } from "date-fns";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Add,
  CalendarToday,
  CheckCircle,
  DeleteOutline,
  Edit,
  List as ListIcon,
  NotificationsActive,
  OpenInNew,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import AgentTaskModal from "../../components/AgentTaskModal";
import TasksCalendar from "../../components/TasksCalendar";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    overflow: "auto",
    borderRadius: 12,
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  taskCard: {
    marginBottom: theme.spacing(1.5),
    borderLeft: "4px solid",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[3],
    },
  },
  overdue: { borderLeftColor: "#DC2626" },
  today: { borderLeftColor: "#D97706" },
  upcoming: { borderLeftColor: "#2563EB" },
  completed: { borderLeftColor: "#059669", opacity: 0.85 },
  empty: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  statChip: {
    fontWeight: 600,
  },
}));

const getTaskStyle = (task, classes) => {
  if (task.status === "completed") return classes.completed;
  const date = parseISO(task.remindAt);
  if (isPast(date) && task.status === "pending") return classes.overdue;
  if (isToday(date)) return classes.today;
  return classes.upcoming;
};

const getDateLabel = (task) => {
  const date = parseISO(task.remindAt);
  if (task.status === "completed") return i18n.t("tasks.status.completed");
  if (isPast(date)) return i18n.t("tasks.status.overdue");
  if (isToday(date)) return i18n.t("tasks.status.today");
  if (isTomorrow(date)) return i18n.t("tasks.status.tomorrow");
  return format(date, "dd/MM/yyyy HH:mm");
};

const Tasks = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [viewMode, setViewMode] = useState("list");
  const [modalOpen, setModalOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [presetRemindAt, setPresetRemindAt] = useState(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const status =
        viewMode === "calendar" ? "all" : tab === "all" ? "all" : tab;
      const { data } = await api.get("/agent-tasks", { params: { status } });
      setTasks(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [tab, viewMode]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!user?.companyId || !socket) return;

    const onAgentTask = (data) => {
      if (Number(data.userId) !== Number(user.id)) return;

      if (data.action === "reminder") {
        toast.info(
          <Box>
            <strong>{data.task?.title}</strong>
            <div>{data.task?.body}</div>
          </Box>,
          { autoClose: 8000, icon: <NotificationsActive /> }
        );

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(data.task?.title || i18n.t("tasks.reminder"), {
            body: data.task?.body || "",
            icon: "/favicon.ico",
          });
        }
        loadTasks();
      }

      if (["create", "update", "delete"].includes(data.action)) {
        loadTasks();
      }
    };

    socket.on(`company-${user.companyId}-agentTask`, onAgentTask);
    return () => {
      socket.off(`company-${user.companyId}-agentTask`, onAgentTask);
    };
  }, [socket, user, loadTasks]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const stats = useMemo(() => {
    const pending = tasks.filter((t) => t.status === "pending");
    const overdue = pending.filter((t) => isPast(parseISO(t.remindAt)));
    const today = pending.filter((t) => isToday(parseISO(t.remindAt)));
    return { pending: pending.length, overdue: overdue.length, today: today.length };
  }, [tasks]);

  const handleComplete = async (task) => {
    try {
      await api.put(`/agent-tasks/${task.id}`, { status: "completed" });
      toast.success(i18n.t("tasks.completed"));
      loadTasks();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (task) => {
    try {
      await api.delete(`/agent-tasks/${task.id}`);
      toast.success(i18n.t("tasks.deleted"));
      loadTasks();
    } catch (err) {
      toastError(err);
    }
  };

  const openEdit = (task) => {
    setEditTaskId(task.id);
    setModalOpen(true);
  };

  const openCreate = (date = null) => {
    setEditTaskId(null);
    if (date) {
      const d = new Date(date);
      if (d.getHours() === 0 && d.getMinutes() === 0) {
        d.setHours(9, 0, 0, 0);
      }
      setPresetRemindAt(d);
    } else {
      setPresetRemindAt(null);
    }
    setModalOpen(true);
  };

  const goToTicket = (task) => {
    if (task.ticket?.uuid) {
      history.push(`/tickets/${task.ticket.uuid}`);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("tasks.title")}</Title>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => openCreate()}
        >
          {i18n.t("tasks.new")}
        </Button>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          <ButtonGroup className={classes.viewToggle} size="small" variant="outlined" color="primary">
            <Button
              variant={viewMode === "list" ? "contained" : "outlined"}
              startIcon={<ListIcon />}
              onClick={() => setViewMode("list")}
            >
              {i18n.t("tasks.views.list")}
            </Button>
            <Button
              variant={viewMode === "calendar" ? "contained" : "outlined"}
              startIcon={<CalendarToday />}
              onClick={() => setViewMode("calendar")}
            >
              {i18n.t("tasks.views.calendar")}
            </Button>
          </ButtonGroup>
        </Box>

        <Box className={classes.statsRow}>
          <Chip
            className={classes.statChip}
            label={`${i18n.t("tasks.stats.pending")}: ${stats.pending}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            className={classes.statChip}
            label={`${i18n.t("tasks.stats.today")}: ${stats.today}`}
            style={{ borderColor: "#D97706", color: "#D97706" }}
            variant="outlined"
          />
          <Chip
            className={classes.statChip}
            label={`${i18n.t("tasks.stats.overdue")}: ${stats.overdue}`}
            style={{ borderColor: "#DC2626", color: "#DC2626" }}
            variant="outlined"
          />
        </Box>

        {viewMode === "list" && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: 16 }}
          >
            <Tab label={i18n.t("tasks.tabs.pending")} value="pending" />
            <Tab label={i18n.t("tasks.tabs.completed")} value="completed" />
            <Tab label={i18n.t("tasks.tabs.all")} value="all" />
          </Tabs>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : viewMode === "calendar" ? (
          <Box className={classes.calendarWrapper}>
            <TasksCalendar
              tasks={tasks}
              onEdit={openEdit}
              onComplete={handleComplete}
              onDelete={handleDelete}
              onGoToTicket={goToTicket}
              onCreateForDate={openCreate}
            />
          </Box>
        ) : tasks.length === 0 ? (
          <Box className={classes.empty}>
            <NotificationsActive style={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              {i18n.t("tasks.empty")}
            </Typography>
            <Typography variant="body2">{i18n.t("tasks.emptyHint")}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {tasks.map((task) => (
              <Grid item xs={12} md={6} key={task.id}>
                <Card
                  className={`${classes.taskCard} ${getTaskStyle(task, classes)}`}
                  variant="outlined"
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                          {task.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={getDateLabel(task)}
                          style={{ marginTop: 8, marginBottom: 8 }}
                        />
                        {task.body && (
                          <Typography variant="body2" color="textSecondary" paragraph>
                            {task.body}
                          </Typography>
                        )}
                        {task.contact?.name && (
                          <Typography variant="caption" display="block">
                            {i18n.t("tasks.contact")}: {task.contact.name}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" flexDirection="column">
                        {task.status === "pending" && (
                          <IconButton size="small" onClick={() => handleComplete(task)} title={i18n.t("tasks.markDone")}>
                            <CheckCircle style={{ color: "#059669" }} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => openEdit(task)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        {task.ticket?.uuid && (
                          <IconButton size="small" onClick={() => goToTicket(task)}>
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleDelete(task)}>
                          <DeleteOutline fontSize="small" style={{ color: "#DC2626" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <AgentTaskModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditTaskId(null);
          setPresetRemindAt(null);
        }}
        taskId={editTaskId}
        initialRemindAt={presetRemindAt}
        onSaved={loadTasks}
      />
    </MainContainer>
  );
};

export default Tasks;
